/**
 * WebSocket client pro painel admin web.
 *
 * Mantém 1 conexão persistente com `/api/ws/admin?token=<jwt>`.
 * Reconnect automático com backoff exponencial.
 * Expõe API de subscribe pra eventos específicos.
 *
 * Eventos recebidos:
 *   - dispatch:new, dispatch:cancel, dispatch:status
 *   - service:create, service:update, service:delete
 */
import { useEffect, useState } from "react";

const BACKOFF_MS = [1000, 2000, 4000, 8000, 16000, 30000];

const handlers = new Map();
let socket = null;
let reconnectTimer = null;
let reconnectAttempt = 0;
let connectedAt = null;
let stopped = false;
const statusListeners = new Set();

// ─── Vigia de conexão morta-viva ────────────────────────────────────
// O backend manda `ping` a cada 25s. Se nada chega há mais de 60s, a
// conexão está "half-open": o `readyState` continua 1 (o painel mostra
// "Tempo real" verde), mas nenhum evento chega mais — era isso que
// deixava o marcador congelado até o operador recarregar a página.
// Proxy que mata idle, suspensão do notebook e troca de rede causam isso
// sem disparar `onclose`.
const STALE_MS = 60000;
const WATCHDOG_EVERY_MS = 10000;
let lastMsgAt = 0;
let watchdogTimer = null;

let ultimoStatusPublicado = false;

function snapshotStatus() {
  return { connected: socket?.readyState === 1, connectedAt };
}

function notifyStatus() {
  const snap = snapshotStatus();
  ultimoStatusPublicado = snap.connected;
  statusListeners.forEach((fn) => {
    try {
      fn(snap);
    } catch {
      /* noop */
    }
  });
}

function buildWsUrl() {
  const base = process.env.REACT_APP_BACKEND_URL;
  if (!base) return null;
  const wsBase = base.replace(/^http(s?):/, "ws$1:");
  // No web, browser anexa cookie HTTPOnly automaticamente — não precisa token na query
  return `${wsBase}/api/ws/admin`;
}

function dispatch(msg) {
  const set = handlers.get(msg.event);
  if (set) {
    set.forEach((fn) => {
      try {
        fn(msg.data || {});
      } catch (e) {
         
        console.error("[WS] handler error", e);
      }
    });
  }
}

function connect() {
  if (socket && (socket.readyState === 0 || socket.readyState === 1)) return;
  const url = buildWsUrl();
  if (!url) return;
  stopped = false;
  let ws;
  try {
    ws = new WebSocket(url);
  } catch (e) {
     
    console.warn("[WS] connect throw:", e);
    scheduleReconnect();
    return;
  }
  socket = ws;
  let abriu = false;
  // Todos os handlers checam `socket !== ws`: um socket antigo (já
  // substituído por outro) não pode mexer no estado global nem agendar
  // reconexão — era isso que deixava DUAS conexões abertas quando o
  // efeito remontava (StrictMode/troca de usuário): o `onclose` do socket
  // velho zerava a referência do novo e o backoff abria um terceiro.
  ws.onopen = () => {
    if (socket !== ws) return;
    abriu = true;
    reconnectAttempt = 0;
    connectedAt = Date.now();
    lastMsgAt = Date.now();
     
    console.log("[WS] admin OPEN");
    notifyStatus();
  };
  ws.onmessage = (e) => {
    if (socket !== ws) return;
    lastMsgAt = Date.now();
    // Chegou evento: a conexão está viva. Se o último status publicado dizia
    // o contrário (o `onopen` aconteceu antes de alguém escutar), corrige —
    // era isso que deixava o selo em "Offline" com os eventos chegando.
    if (!ultimoStatusPublicado) notifyStatus();
    try {
      const msg = JSON.parse(e.data);
      if (msg.event !== "ping") {
         
        console.log(`[WS] recv ${msg.event}`, msg.data);
      }
      dispatch(msg);
    } catch {
      /* ignore */
    }
  };
  ws.onerror = () => {
    /* will trigger onclose */
  };
  ws.onclose = (e) => {
    if (socket !== ws) return;
     
    console.log(`[WS] admin CLOSE code=${e.code}`);
    socket = null;
    connectedAt = null;
    notifyStatus();
    if (stopped) return;
    // Conexão recusada no aperto de mão (nunca abriu): o motivo mais comum é o
    // cookie de acesso vencido — ele dura 15 min e o servidor recusa com 403,
    // que chega aqui como 1006. Renova a sessão e tenta de novo, senão o painel
    // ficava em polling com o selo "Offline" até alguém recarregar a página.
    if (!abriu && podeRenovar()) {
      renovarEReconectar();
      return;
    }
    scheduleReconnect();
  };
}

// Uma tentativa de renovação por minuto: servidor fora do ar também cai aqui.
const RENOVA_CADA_MS = 60000;
let renovando = false;
let ultimaRenovacao = 0;
// Na impersonation clássica o cookie de renovação pertence ao admin da
// plataforma: renovar trocaria a identidade da sessão.
let renovacaoPermitida = true;

function podeRenovar() {
  if (!renovacaoPermitida || renovando) return false;
  return Date.now() - ultimaRenovacao > RENOVA_CADA_MS;
}

async function renovarEReconectar() {
  if (renovando || stopped) return;
  renovando = true;
  ultimaRenovacao = Date.now();
  try {
    const { default: api } = await import("../utils/api");
    await api.post("/auth/refresh");
     
    console.log("[WS] sessão renovada, reconectando");
    reconnectAttempt = 0;
    renovando = false;
    if (!stopped) connect();
  } catch {
    // Sem renovar não há o que fazer além de seguir tentando no backoff.
     
    console.warn("[WS] renovação da sessão falhou");
    renovando = false;
    scheduleReconnect();
  }
}

function scheduleReconnect() {
  if (stopped) return;
  if (reconnectTimer) clearTimeout(reconnectTimer);
  const delay = BACKOFF_MS[Math.min(reconnectAttempt, BACKOFF_MS.length - 1)];
  reconnectAttempt += 1;
   
  console.log(`[WS] reconnect in ${delay}ms`);
  reconnectTimer = setTimeout(connect, delay);
}

/** Descarta o socket atual e reconecta na hora (sem backoff). */
function forceReconnect(motivo) {
   
  console.warn(`[WS] reconectando: ${motivo}`);
  const ws = socket;
  socket = null;
  connectedAt = null;
  if (ws) {
    ws.onopen = null;
    ws.onmessage = null;
    ws.onerror = null;
    ws.onclose = null;
    try {
      ws.close();
    } catch {
      /* ignore */
    }
  }
  notifyStatus();
  reconnectAttempt = 0;
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  if (!stopped) connect();
}

function checkLiveness() {
  if (stopped) return;
  if (!socket) return; // sem socket o backoff já cuida
  if (socket.readyState !== 1) return;
  if (lastMsgAt && Date.now() - lastMsgAt > STALE_MS) {
    forceReconnect(`sem mensagens há ${Math.round((Date.now() - lastMsgAt) / 1000)}s`);
  }
}

function onVisible() {
  if (document.visibilityState !== "visible" || stopped) return;
  // Voltando do segundo plano/suspensão: se o socket morreu calado ou o
  // backoff estava esperando, tenta agora — o operador está olhando.
  if (!socket || socket.readyState > 1) forceReconnect("aba voltou ao foco");
  else checkLiveness();
}

function startWatchdog() {
  if (watchdogTimer) return;
  watchdogTimer = setInterval(checkLiveness, WATCHDOG_EVERY_MS);
  document.addEventListener("visibilitychange", onVisible);
}

function stopWatchdog() {
  if (watchdogTimer) {
    clearInterval(watchdogTimer);
    watchdogTimer = null;
  }
  document.removeEventListener("visibilitychange", onVisible);
}

// API pública
export function startAdminWS({ podeRenovarSessao = true } = {}) {
  stopped = false;
  renovacaoPermitida = podeRenovarSessao;
  startWatchdog();
  if (socket && (socket.readyState === 0 || socket.readyState === 1)) return;
  connect();
}

export function stopAdminWS() {
  stopped = true;
  stopWatchdog();
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  const ws = socket;
  socket = null;
  connectedAt = null;
  if (ws) {
    // Desliga os handlers ANTES de fechar: o `onclose` tardio não pode
    // agendar reconexão nem sobrescrever um socket novo.
    ws.onopen = null;
    ws.onmessage = null;
    ws.onerror = null;
    ws.onclose = null;
    try {
      ws.close();
    } catch {
      /* ignore */
    }
  }
  notifyStatus();
}

export function subscribeAdminWS(event, fn) {
  if (!handlers.has(event)) handlers.set(event, new Set());
  handlers.get(event).add(fn);
  return () => handlers.get(event)?.delete(fn);
}

/** Envia um evento pro backend. `false` quando o socket não está aberto. */
export function sendAdminWS(event, data) {
  if (socket?.readyState !== 1) return false;
  try {
    socket.send(JSON.stringify({ event, data: data || {} }));
    return true;
  } catch {
    return false;
  }
}

export function useAdminWS(event, fn) {
  useEffect(() => {
    return subscribeAdminWS(event, fn);
     
  }, [event]);
}

export function useAdminWSStatus() {
  const [st, setSt] = useState(snapshotStatus);
  useEffect(() => {
    statusListeners.add(setSt);
    // O socket pode ter aberto entre o render e este efeito: sem reler o
    // estado agora, o selo ficaria "Offline" até a próxima troca.
    setSt(snapshotStatus());
    return () => {
      statusListeners.delete(setSt);
    };
  }, []);
  return st;
}
